// A2-582 — the answers that are not given on the card: fix it in the client
// record, Settings or Step 2 (one change clears every row on the next run),
// correct the file, or — where the app has no way out yet — say so plainly.
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorkflowOptional } from "../../workflow/WorkflowContext";
import { useAuth } from "../../auth/AuthContext";

const BTN = "inline-flex items-center text-xs px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800";

const GOTO_DEFAULTS = {
  "client.establishment": "Set the establishment country in the client record ↗",
  "client.filing": "Set the filing model in the client record ↗",
  "client.ioss": "Set up IOSS in the client record ↗",
  "client.oss": "Open the One Stop Shop details in the client record ↗",
  // A2-591 — the server names the scheme this client could join for the row.
  "client.oss.UNION": "Set up the EU One Stop Shop (Union scheme) in the client record ↗",
  "client.oss.NON_UNION": "Join the One Stop Shop for non-EU businesses in the client record ↗",
  "client.vatregs": "Check the VAT registrations in the client record ↗",
  "settings.rates": "Open Settings › Rate tables",
  "step2.fx": "Enter the exchange rate in Step 2",
  "step2.upload": "Check the files in Step 2",
  "review.vies_queue": "Open the VAT-number check queue",
};

const NOTE = "text-xs text-slate-600 dark:text-slate-300";

// A2-581 leads 7-9: an answer this user cannot give (the client record and
// Settings are admin-only; a calc with no client has no client record) used to
// be a link that bounced back. It is said instead, so the card is never a dead
// end. The VAT-number queue is open to every signed-in user (since v0.6.10,
// api/routes_vies_override), so it stays a button for everyone.
export const AdminOnlyNote = ({ label, testid = "answer-admin-only" }) => {
  const { t } = useTranslation();
  return (
    <span data-testid={testid} className={NOTE}>
      {t("ui.review_answers.admin_only", {
        what: label.replace(/\s*↗$/, ""),
        defaultValue: "Only an admin can do this ({{what}}). Ask an admin to make the change, then run the calculation again.",
      })}
    </span>
  );
};

export const NoClientNote = () => {
  const { t } = useTranslation();
  return (
    <span data-testid="answer-no-client" className={NOTE}>
      {t("ui.review_answers.no_client", {
        defaultValue: "This calculation is not linked to a client, so there is no client record to change.",
      })}
    </span>
  );
};

export const GoToAnswer = ({ answer, clientId }) => {
  const { t } = useTranslation();
  const wf = useWorkflowOptional();
  const isAdmin = (useAuth() || {}).admin?.role === "ADMIN";
  const target = answer.target;
  const key = answer.scheme ? `${target}.${answer.scheme}` : target;
  const label = t(`ui.review_answers.goto.${key.replace(/\./g, "_").toLowerCase()}`, { defaultValue: GOTO_DEFAULTS[key] || GOTO_DEFAULTS[target] || target });
  const testid = `answer-goto-${target}`;
  if (target.startsWith("client.")) {
    if (!clientId) return <NoClientNote />;
    if (!isAdmin) return <AdminOnlyNote label={label} />;
    // The operator asked for the client record in a new tab (2026-10-27),
    // so the review page and its place stay where they were.
    return (
      <a href={`/admin/clients?edit=${encodeURIComponent(clientId)}&section=${target.slice(7)}`}
         target="_blank" rel="noopener noreferrer" data-testid={testid} className={BTN}>{label}</a>
    );
  }
  if (target === "settings.rates") {
    if (!isAdmin) return <AdminOnlyNote label={label} />;
    return <Link to="/settings?tab=rate_tables" data-testid={testid} className={BTN}>{label}</Link>;
  }
  if (target.startsWith("step2.")) {
    return (
      <button type="button" data-testid={testid} className={BTN}
              onClick={() => (wf?.setActiveStep ? wf.setActiveStep(2) : window.location.assign("/workflow/configure"))}>
        {label}
      </button>
    );
  }
  if (target === "review.vies_queue") return <ViesQueueLink label={label} testid={testid} />;
  return null;
};

// The queue shows only while it holds rows; with none, the click said nothing.
const ViesQueueLink = ({ label, testid }) => {
  const { t } = useTranslation();
  const [empty, setEmpty] = React.useState(false);
  const go = () => {
    const el = document.querySelector('[data-testid="vies-held-section"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else setEmpty(true);
  };
  return (
    <>
      <button type="button" data-testid={testid} className={BTN} onClick={go}>{label}</button>
      {empty && (
        <span data-testid="answer-vies-queue-empty" role="status" className={`${NOTE} ml-2`}>
          {t("ui.review_answers.vies_queue_empty", {
            defaultValue: "No sale on this calculation is waiting in the VAT-number check queue. If this card still lists rows, run the calculation again.",
          })}
        </span>
      )}
    </>
  );
};

export const FixInFileAnswer = ({ answer }) => {
  const { t } = useTranslation();
  const columns = (answer.columns || []).join(", ");
  return (
    <span data-testid="answer-fix-in-file" className="text-xs text-slate-600 dark:text-slate-300">
      {t("ui.review_answers.fix_in_file", {
        columns, defaultValue: "Correct {{columns}} in the file, upload it again, then re-run.",
      })}
    </span>
  );
};

export const NoWayOutAnswer = () => {
  const { t } = useTranslation();
  return (
    <span data-testid="answer-no-way-out" className="text-xs text-slate-600 dark:text-slate-300">
      {t("ui.review_answers.no_way_out", {
        defaultValue: "The app cannot record the answer to this yet. Ask for help before filing; if the sale should not be filed, exclude it under Excluded rows.",
      })}
    </span>
  );
};
